import { useEffect, useRef, useState } from "react";
import {
  Badge,
  Box,
  IconButton,
  Popover,
  Tooltip,
  Typography,
} from "@mui/material";
import ForumOutlined from "@mui/icons-material/ForumOutlined";
import SendRounded from "@mui/icons-material/SendRounded";
import { io } from "socket.io-client";
import { useAuth } from "../context/authContext";
import { API_CONFIG } from "../utils/constants";

const ROOM = "sala";

const palette = {
  text: "#334155",
  textMuted: "#94a3b8",
  textActive: "#0f172a",
  border: "rgba(15, 23, 42, 0.06)",
  hover: "rgba(15, 23, 42, 0.04)",
  accent: "#6366f1",
  accentSoft: "rgba(99, 102, 241, 0.08)",
};

const ROLES = new Set(["admin", "supervisor", "scheduler", "technician"]);

function chatRole(account) {
  return ROLES.has(account) ? account : "technician";
}

function fmtTime(d) {
  if (!d) return "";
  return new Intl.DateTimeFormat("es-MX", {
    hour: "2-digit",
    minute: "2-digit",
    timeZone: "America/Mexico_City",
  }).format(new Date(d));
}

export default function GroupChat() {
  const { user, isAuthenticated } = useAuth();
  const [anchorEl, setAnchorEl] = useState(null);
  const [online, setOnline] = useState(0);
  const [messages, setMessages] = useState([]);
  const [draft, setDraft] = useState("");
  const [connected, setConnected] = useState(false);
  const socketRef = useRef(null);
  const listRef = useRef(null);
  const open = Boolean(anchorEl);

  const shown = Math.max(1, online + 1);

  useEffect(() => {
    const onCount = (event) => setOnline(Number(event.detail) || 0);
    window.addEventListener("trazia-online", onCount);
    return () => window.removeEventListener("trazia-online", onCount);
  }, []);

  useEffect(() => {
    if (!isAuthenticated || !user) return undefined;

    const socket = io(API_CONFIG.SOCKET_URL, {
      query: {
        userName: user.name || "Alguien",
        userRole: chatRole(user.account),
        room: ROOM,
      },
      transports: ["websocket", "polling"],
    });
    socketRef.current = socket;

    socket.on("connect", () => setConnected(true));
    socket.on("disconnect", () => setConnected(false));
    socket.on("history", (history) => setMessages(Array.isArray(history) ? history : []));
    socket.on("message", (msg) => {
      if (!msg || msg.room !== ROOM) return;
      setMessages((prev) => (prev.some((item) => item._id === msg._id) ? prev : [...prev, msg]));
    });

    return () => {
      socket.disconnect();
      socketRef.current = null;
    };
  }, [isAuthenticated, user]);

  useEffect(() => {
    if (!open || !listRef.current) return;
    listRef.current.scrollTop = listRef.current.scrollHeight;
  }, [open, messages]);

  const send = () => {
    const text = draft.trim();
    if (!text || !socketRef.current) return;
    socketRef.current.emit("sendMessage", { message: text.slice(0, 500), type: "text" });
    setDraft("");
  };

  if (!isAuthenticated) return null;

  return (
    <>
      <Tooltip title="Chat del equipo" arrow>
        <IconButton
          onClick={(event) => setAnchorEl(event.currentTarget)}
          aria-label="Chat del equipo"
          sx={{
            color: open ? palette.accent : palette.text,
            borderRadius: "12px",
            bgcolor: open ? palette.accentSoft : "transparent",
            "&:hover": { backgroundColor: palette.hover },
          }}
        >
          <Badge
            badgeContent={shown}
            max={99}
            overlap="circular"
            sx={{
              "& .MuiBadge-badge": {
                bgcolor: "#16a34a",
                color: "#fff",
                fontSize: 10,
                fontWeight: 700,
                minWidth: 16,
                height: 16,
                px: 0.5,
                boxShadow: "0 0 0 2px #ffffff",
              },
            }}
          >
            <ForumOutlined sx={{ fontSize: 22 }} />
          </Badge>
        </IconButton>
      </Tooltip>

      <Popover
        open={open}
        anchorEl={anchorEl}
        onClose={() => setAnchorEl(null)}
        transformOrigin={{ horizontal: "right", vertical: "top" }}
        anchorOrigin={{ horizontal: "right", vertical: "bottom" }}
        slotProps={{
          paper: {
            sx: {
              mt: 1,
              width: { xs: "calc(100vw - 24px)", sm: 360 },
              maxWidth: 360,
              borderRadius: "16px",
              border: `1px solid ${palette.border}`,
              boxShadow: "0 16px 40px rgba(15, 23, 42, 0.12)",
              overflow: "hidden",
            },
          },
        }}
      >
        <Box sx={{ px: 2, py: 1.5, borderBottom: `1px solid ${palette.border}` }}>
          <Typography sx={{ fontSize: 14, fontWeight: 700, color: palette.textActive }}>
            Chat del equipo
          </Typography>
          <Box sx={{ display: "flex", alignItems: "center", gap: 0.75, mt: 0.5 }}>
            <Box
              sx={{
                width: 8,
                height: 8,
                borderRadius: "50%",
                bgcolor: "#16a34a",
                boxShadow: "0 0 0 3px rgba(22, 163, 74, 0.18)",
                animation: "chatPulse 1.8s ease-in-out infinite",
                "@keyframes chatPulse": {
                  "0%, 100%": { opacity: 1 },
                  "50%": { opacity: 0.45 },
                },
              }}
            />
            <Typography sx={{ fontSize: 12, color: palette.textMuted }}>
              {shown} {shown === 1 ? "persona activa" : "personas activas"}
            </Typography>
          </Box>
        </Box>

        <Box
          ref={listRef}
          sx={{
            height: 320,
            overflowY: "auto",
            px: 1.5,
            py: 1.25,
            display: "flex",
            flexDirection: "column",
            gap: 1,
            bgcolor: "#f8fafc",
          }}
        >
          {messages.length === 0 && (
            <Typography sx={{ fontSize: 12.5, color: palette.textMuted, textAlign: "center", mt: 4 }}>
              La sala está abierta. Escribe el primer mensaje.
            </Typography>
          )}
          {messages.map((msg) => {
            const mine = msg.userName === (user?.name || "Alguien");
            return (
              <Box
                key={msg._id || `${msg.createdAt}-${msg.message}`}
                sx={{ alignSelf: mine ? "flex-end" : "flex-start", maxWidth: "85%" }}
              >
                {!mine && (
                  <Typography sx={{ fontSize: 11, fontWeight: 600, color: palette.accent, mb: 0.25, px: 0.5 }}>
                    {msg.userName}
                  </Typography>
                )}
                <Box
                  sx={{
                    px: 1.25,
                    py: 0.75,
                    borderRadius: mine ? "14px 14px 4px 14px" : "14px 14px 14px 4px",
                    bgcolor: mine ? palette.accent : "#fff",
                    color: mine ? "#fff" : palette.textActive,
                    border: mine ? "none" : `1px solid ${palette.border}`,
                  }}
                >
                  <Typography sx={{ fontSize: 13, lineHeight: 1.4, whiteSpace: "pre-wrap" }}>
                    {msg.message}
                  </Typography>
                </Box>
                <Typography sx={{ fontSize: 10, color: palette.textMuted, mt: 0.25, px: 0.5, textAlign: mine ? "right" : "left" }}>
                  {fmtTime(msg.createdAt)}
                </Typography>
              </Box>
            );
          })}
        </Box>

        <Box
          component="form"
          onSubmit={(event) => {
            event.preventDefault();
            send();
          }}
          sx={{
            display: "flex",
            gap: 1,
            p: 1.25,
            borderTop: `1px solid ${palette.border}`,
            bgcolor: "#fff",
          }}
        >
          <Box
            component="input"
            value={draft}
            onChange={(event) => setDraft(event.target.value)}
            placeholder={connected ? "Escribe al equipo…" : "Conectando…"}
            disabled={!connected}
            maxLength={500}
            sx={{
              flex: 1,
              border: `1px solid ${palette.border}`,
              borderRadius: "12px",
              px: 1.5,
              py: 1,
              fontFamily: "inherit",
              fontSize: 13,
              outline: "none",
              "&:focus": { borderColor: palette.accent },
            }}
          />
          <IconButton
            type="submit"
            disabled={!connected || !draft.trim()}
            aria-label="Enviar"
            sx={{
              bgcolor: palette.accent,
              color: "#fff",
              borderRadius: "12px",
              "&:hover": { bgcolor: "#4f46e5" },
              "&.Mui-disabled": { bgcolor: palette.accentSoft, color: palette.textMuted },
            }}
          >
            <SendRounded sx={{ fontSize: 18 }} />
          </IconButton>
        </Box>
      </Popover>
    </>
  );
}
